// React&NextJS
import { useEffect, useState } from 'react'
import { GetServerSideProps } from 'next'
import router from 'next/router'
// Context&Auth
import { useAuth } from '../../context/UserContext'
import withAuth from '../../context/withAuth'
// 3rd party libraries
import { motion } from 'framer-motion'
// Services&Helper functions
import firestoreDeleteService from '../../services/delete'
import read from '../../services/read'
import helperFunctions from '../../utils/helperFunctions'
import memoryHelperFunctions from '../../utils/memoryHelperFunctions'
// Types
import { Memory, ObituaryPlate, Settings } from '../../types/types'
// Styles
import s from '../../styles/pages/Account.module.scss'
// Components
import ActionModal from '../../components/components/modals/ActionModal'
import Breadcrumbs from '../../components/components/breadcrumbs/Breadcumbs'
import { Button } from '../../components/core/button/Button'
import { Container } from '../../components/core/container/Container'
import Meta from '../../components/meta/Meta'
import { Section } from '../../components/core/section/Section'
import { Table } from '../../components/components/table/Table'
import { Tabs } from '../../components/core/tabs/Tabs'
import { Text } from '../../components/core/text/Text'
import UserMemories from '../../components/components/account/memories/UserMemories'
import UserProfile from '../../components/components/account/profile/UserProfile'
import { Filters } from '../../components/core/filters/Filters'
import BackToTop from '../../components/components/backToTop/BackToTop'

type selectedObitType = {
    o: ObituaryPlate
    i: number
}

type ActionModalInfo = {
    title: string
    info: string
    action: string
}

type FollowedMem = Memory & {
    readOnly: boolean
}

const DELETEINFORMATION =
    "Ef þú smellir á 'Staðfesta' mun greinin eyðast af vefnum okkar. Þú munt ekki geta endurheimt hana aftur eftir þessa aðgerð. Ertu viss um að þú viljir eyða þessari grein?"

export const getServerSideProps: GetServerSideProps = async ({ req }) => {
    try {
        let authData = {}
        // Keys from from auth cookies
        const authKeys = ['create', 'customToken', 'success', 'ssn', 'message']
        if (Object.keys(req.cookies).length > 0) {
            Object.entries(req.cookies).forEach(([key, value]) => {
                // Parse cookies fron authData
                if (authKeys.includes(key)) {
                    authData[key] = JSON.parse(value)
                }
            })
        }
        // Return data as props if size > 0
        const hasData = Object.keys(authData).length > 0

        return { props: { authData: hasData ? authData : null } }
    } catch (error) {
        console.error(error)
    }

    return { props: { authData: null } }
}

function Account({ authData }) {
    const { user } = useAuth()

    const [tab, setTab] = useState<String | Boolean>('')
    const [isLoaded, setIsLoaded] = useState<Boolean>(false)
    const [memories, setMemories] = useState<Array<Memory>>([])
    const [followedMems, setFollowedMems] = useState<Array<FollowedMem>>([])
    const [obituaries, setObituaries] = useState<Array<ObituaryPlate>>([])
    const [obitFilter, setObitFilter] = useState(0)
    const [settings, setSettings] = useState<Settings>(null)
    const [selectedObit, setSelectedObit] = useState<selectedObitType>(null)
    const [actionInfo, setActionInfo] = useState<ActionModalInfo>(null)
    const [openActionModal, setOpenActionModal] = useState(false)
    const [, setOpenReqModal] = useState(false)
    const [firstLoad, setFirstLoad] = useState(true)
    const [backToTop, setbackToTop] = useState(false)
    const [followPage, setFollowPage] = useState(1)
    const [moreFollowing, setMoreFollowing] = useState(true)
    const [followLoad, setFollowLoad] = useState(false)

    useEffect(() => {
        if (authData) router.reload()
    }, [authData])

    useEffect(() => {
        setIsLoaded(false)

        let isCancelled = false
        // users references to obituaries and memories
        const obitRefs = user?.obituaries
        const memRefs = user?.memories

        const getUserData = async () => {
            // get data
            if (!isCancelled) {
                const getMemories = async () => {
                    return await read.getUsersMems(memRefs)
                }
                const getFollowedMems = async () => {
                    return await read.getFollowedMems(user.uid, 1, 10)
                }
                const getObituaries = async () => {
                    return await read.getUsersObits(obitRefs)
                }
                const getUserSettings = async () => {
                    return await read.getUsersSettings(user)
                }
                const data = await Promise.all([
                    getMemories(),
                    getFollowedMems(),
                    getObituaries(),
                    getUserSettings(),
                ])

                data[0] && setMemories(data[0])
                data[1] && setFollowedMems(data[1])
                data[2] && setObituaries(data[2])
                data[3] && setSettings(data[3])

                setIsLoaded(true)
            }
        }
        // If there is an user, get data
        user && getUserData()

        return () => {
            isCancelled = true
        }
    }, [user])

    useEffect(() => {
        if (window.location.hash) {
            const hash = window.location.hash.replace('#', '')
            if (hash === 'memories') {
                setTab('Minningarsíður')
            } else if (hash === 'obituaries') {
                setTab('Minningargreinar')
            } else if (hash === 'settings') {
                setTab('Prófíll og stillingar')
            }
        } else {
            setTab('Minningarsíður')
        }
    }, [])

    useEffect(() => {
        // const offset = window.innerWidth < 720 ? 650 : 350
        // setbackToTop(window.scrollY >= offset ? true : false)
        const handleScroll = () => {
            const tabsY = document
                .getElementById('tabs')
                .getBoundingClientRect().bottom

            setbackToTop(tabsY * 10 < window.innerHeight / 2)
        }
        handleScroll()
        window.addEventListener('scroll', handleScroll)
        return () => {
            window.removeEventListener('scroll', handleScroll)
        }
    }, [])

    // change hash in uri on tab switch
    useEffect(() => {
        if (tab === 'Minningarsíður') {
            router.replace('/account', '/account#memories', {
                shallow: true,
            })
        } else if (tab === 'Minningargreinar') {
            router.replace('/account', '/account#obituaries', {
                shallow: true,
            })
        } else if (tab === 'Prófíll og stillingar') {
            router.replace('/account', '/account#settings', {
                shallow: true,
            })
        }
    }, [tab])

    const updateAnnouncements = async () => {
        const newUserSettings = await read.getUsersSettings(user)
        newUserSettings && setSettings({ ...newUserSettings })
    }

    const allowDeletion = () => {
        deleteObituary(selectedObit.o, selectedObit.i)
        setOpenActionModal((prev) => !prev)
    }

    const deleteObituary = async (obit: ObituaryPlate, index: number) => {
        if (selectedObit) {
            // Delete from firebase
            const mid = obit.memory.mid

            await firestoreDeleteService.deleteObituary(obit, mid)
            // Update state
            const obits = obituaries.filter((o) => o.oid !== obit.oid)
            setObituaries([...obits])
            setSelectedObit(null)
            setActionInfo(null)
        } else {
            setSelectedObit({ o: obit, i: index })
            setActionInfo({
                title: 'Ertu viss?',
                info: DELETEINFORMATION,
                action: 'deleteObit',
            })
            setOpenActionModal(true)
        }
    }

    const actionFunc = () => {
        if (actionInfo?.action === 'deleteObit') {
            allowDeletion()
        }
    }

    const mapObituaries = (obitFilter: number) => {
        // mapper maps obituaries in a format for Table component
        // it also filters it according to filter number
        let mappedObits = []
        if (obitFilter === 0) mappedObits = obituaries
        else {
            mappedObits = obituaries.filter((o) => !o.date)
        }

        return mappedObits.map((o, i) => {
            const date = o.date
                ? helperFunctions.getFancyDateFormat(o.date)
                : 'Ekki birt'
            const obitMem = o.memory
            const deceased = `${obitMem.fullName}`
            // Create title based on data
            const titleInText = o.text && o.text[0]?.type === 'h6'
            const title = o.title
                ? o.title
                : titleInText
                ? o.text[0]?.children[0]?.text || ''
                : ''

            return {
                id: o.oid,
                col1: <Text variant="h6">{deceased}</Text>,
                col2: <Text variant="h6">{title || 'Enginn titill'}</Text>,
                col3: <Text variant="h6">{date}</Text>,
                buttons: [
                    {
                        label: 'Skoða',
                        to: `/account/obituaries/`,
                        after: '',
                    },
                    {
                        label: 'Breyta',
                        to: `/account/obituaries/`,
                        after: '/form',
                    },
                    {
                        label: 'Eyða',
                        func: () => deleteObituary(o, i),
                    },
                ],
            }
        })
    }

    const unfollowMemory = (mid: string) => {
        const updatedMems = followedMems.filter((m) => m.mid !== mid)
        setFollowedMems(updatedMems)
        memoryHelperFunctions.changeFollowState(false, mid, user.uid)
    }

    const getMemLength = () => {
        const memLength = memories?.length || 0
        const followedLength = followedMems?.length || 0
        return memLength + followedLength
    }

    const fetchFollowPage = async () => {
        console.log('Here', followLoad)
        if (!followLoad) {
            setFollowLoad(true)

            const nextPage = followPage + 1
            const followingList = await read.getFollowedMems(
                user.uid,
                nextPage,
                10
            )
            setTimeout(() => {
                setFollowedMems([...followedMems, ...followingList])
                setFollowPage(nextPage)
                if (followingList.length < 10) {
                    setMoreFollowing(false)
                }
                setFollowLoad(false)
            }, 0)
        }
    }

    const tabs = () => {
        //minningar
        if (tab === 'Minningarsíður') {
            return (
                <>
                    {isLoaded && (
                        <UserMemories
                            memories={memories}
                            followed={followedMems}
                            fetchFollowData={fetchFollowPage}
                            moreFollowing={moreFollowing}
                            openReqModal={() =>
                                setOpenReqModal((prev: boolean) => !prev)
                            }
                            unfollowMemory={(mid: string) => {
                                unfollowMemory(mid)
                            }}
                            fadeIn={firstLoad}
                        />
                    )}
                </>
            )
        }
        //minningargreinar
        else if (tab === 'Minningargreinar') {
            if (isLoaded && obituaries.length === 0) {
                return (
                    <div className={s.default}>
                        <Text variant="p">
                            Hér geturðu skrifað nýja minningargrein. Einnig
                            hefurðu yfirlit yfir þær greinar sem þú hefur
                            skrifað.
                        </Text>
                        <Button to="/memories" variant="ghost">
                            Skrifa minningargrein
                        </Button>
                    </div>
                )
            }
            return (
                <>
                    {isLoaded && (
                        <>
                            <Filters
                                filterNames={['Allar', 'Óbirtar']}
                                selectedIndex={obitFilter}
                                setState={setObitFilter}
                                className={s.filter}
                            />
                            <Table
                                content={mapObituaries(obitFilter) || []}
                                type={2}
                                fadeIn={false}
                                headers={{
                                    col1: <Text variant="date">Andlát</Text>,
                                    col2: <Text variant="date">Fyrirsögn</Text>,
                                    col3: <Text variant="date">Sent inn</Text>,
                                }}
                            />
                        </>
                    )}
                </>
            )
        }
        // prófíll og stillingar
        else if (tab === 'Prófíll og stillingar') {
            return (
                <>
                    {isLoaded && (
                        <UserProfile
                            settings={settings}
                            updateProfileSettings={updateAnnouncements}
                        />
                    )}
                </>
            )
        }
    }

    return (
        <>
            <ActionModal
                title={actionInfo?.title}
                info={actionInfo?.info}
                isOpen={openActionModal}
                closeModal={() => {
                    setOpenActionModal(false)
                    setActionInfo(null)
                    selectedObit && setSelectedObit(null)
                }}
                submitEvent={actionFunc}
            />
            <Section>
                <Meta
                    title="Minningar - Mitt svæði"
                    seoTitle="Minningar - Mitt svæði"
                    description="Minningarsíður sem að ég hef stofnað og greinar ásamt prófílsstillingum."
                    image="https://ik.imagekit.io/minningar/Minningar_7V4A5213_01-min_5-f7B_aBV.jpg?ik-sdk-version=javascript-1.4.3&updatedAt=1643189239548&tr=w-1200%2Ch-628%2Cfo-auto"
                    type="website"
                    url="https://minningar.is/account"
                />
                <motion.div>
                    <Breadcrumbs
                        breadcrumbs={[
                            { name: 'Mitt svæði', href: '/account' },
                            { name: `${tab}`, href: '/account' },
                        ]}
                    />
                    <Container>
                        <Text className={s.header} variant="h2">
                            Mitt svæði
                        </Text>
                    </Container>
                    <Container className={s.container}>
                        <Tabs
                            id="tabs"
                            className={s.tabs}
                            noFrag={true}
                            tabNames={[
                                'Minningarsíður',
                                'Minningargreinar',
                                'Prófíll og stillingar',
                            ]}
                            selectedTab={tab}
                            setState={(t: string) => {
                                setTab(t)
                                setFirstLoad(false)
                            }}
                        />
                        {tabs()}
                        {getMemLength() >= 4 && (
                            <BackToTop
                                column={12}
                                row={1}
                                span={1000}
                                isActive={backToTop}
                            />
                        )}
                    </Container>
                </motion.div>
                {tab === 'Minningarsíður' && followLoad && (
                    <div className={s.loader}>
                        <div className={s.loaderInside}></div>
                    </div>
                )}
            </Section>
        </>
    )
}

export default withAuth(Account)
